import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi, useApiQuery } from "@/api/useApi";
import type { ExpenseDelegate } from "@/api/types";
import { Icon } from "@/components/Icon";

type Row = { key: number; delegator: string; delegate: string; canApprove: boolean };

let nextKey = 0;
const toRow = (pair: ExpenseDelegate): Row => ({
  key: nextKey++,
  delegator: pair.delegator.email ?? pair.delegator.displayName,
  delegate: pair.delegate.email ?? pair.delegate.displayName,
  canApprove: pair.canApprove,
});

/**
 * Who does whose expenses: a delegate (such as an assistant) switches to working for the person
 * from the account menu, and prepares and sends their reports, which come to that person to
 * approve first. Ticked, they also approve for them.
 */
export function DelegatesPage() {
  const api = useApi();
  const { data, error, loading } = useApiQuery<ExpenseDelegate[]>(
    "/api/expenses/settings/delegates",
  );
  const [rows, setRows] = useState<Row[]>();
  const [result, setResult] = useState<{ ok: boolean; text: string }>();
  const [busy, setBusy] = useState(false);

  if (loading && !data) {
    return (
      <div className="card" aria-busy="true">
        <div className="skeleton line" />
        <div className="skeleton line wide" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="error" role="alert">
        {error?.message ?? "Could not load the delegates."}
      </div>
    );
  }

  const shown = rows ?? data.map(toRow);
  const change = (key: number, patch: Partial<Row>) =>
    setRows(shown.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  function save() {
    setBusy(true);
    setResult(undefined);
    api
      .put<ExpenseDelegate[]>("/api/expenses/settings/delegates", {
        // Every line, blank ones too, so a line that didn't take is an error rather than dropped.
        delegates: shown.map(({ delegator, delegate, canApprove }) => ({
          delegator: delegator.trim(),
          delegate: delegate.trim(),
          canApprove,
        })),
      })
      .then((saved) => {
        setRows(saved.map(toRow));
        setResult({
          ok: true,
          text: "Saved. Delegates see who they can work for in the menu under their initials.",
        });
      })
      .catch((caught: unknown) =>
        setResult({ ok: false, text: caught instanceof Error ? caught.message : String(caught) }),
      )
      .finally(() => setBusy(false));
  }

  const saveButton = (
    <button type="button" className="btn" onClick={save} disabled={busy}>
      {busy ? "Saving…" : "Save"}
    </button>
  );
  const saved = result && (
    <div className={result.ok ? "success" : "error"} role="status">
      {result.text}
    </div>
  );

  return (
    <div className="narrow">
      <Link className="back" to="/settings">
        <Icon name="chevronLeft" size={18} /> Settings
      </Link>
      <div className="page-head">
        <h1>Delegates</h1>
        {saveButton}
      </div>
      {saved}
      <p className="muted lead">
        A delegate does someone&apos;s expenses for them. They switch to working for that person
        from the menu under their initials, and the reports they send go to that person to approve
        before their manager and payroll. Someone who approves their own reports (Settings → Self
        Approvers) sends them straight on to payroll.
      </p>

      {shown.length === 0 && <div className="card empty muted">No delegates yet.</div>}
      {shown.map((row) => (
        <section key={row.key} className="card stack delegate-row">
          <div className="delegate-people">
            <label>
              Delegator
              <input
                type="email"
                value={row.delegator}
                onChange={(e) => change(row.key, { delegator: e.target.value })}
                placeholder="todd@planetart.com"
                autoComplete="off"
              />
            </label>
            <label>
              Delegate
              <input
                type="email"
                value={row.delegate}
                onChange={(e) => change(row.key, { delegate: e.target.value })}
                placeholder="their assistant's sign-in"
                autoComplete="off"
              />
            </label>
          </div>
          <div className="row">
            <label className="check">
              <input
                type="checkbox"
                checked={row.canApprove}
                onChange={(e) => change(row.key, { canApprove: e.target.checked })}
              />
              <span>
                Also approves for them{" "}
                <span className="optional">
                  reports sent to the delegator as a manager or executive
                </span>
              </span>
            </label>
            <button
              type="button"
              className="link-btn"
              onClick={() => setRows(shown.filter((r) => r.key !== row.key))}
            >
              <Icon name="trash" size={16} /> Remove
            </button>
          </div>
        </section>
      ))}

      <button
        type="button"
        className="btn secondary"
        onClick={() =>
          setRows([...shown, { key: nextKey++, delegator: "", delegate: "", canApprove: false }])
        }
      >
        <Icon name="plus" size={18} /> Add a delegate
      </button>

      {saved}
      {saveButton}
    </div>
  );
}
